/** HR 考勤中的加班台账：按月查看本人或有审批权限员工的申请、实际卡与异常。
 * 与个人周历共用详情和后端权限，避免 HR 报表与经理审批各自保存一套工时。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { Loader2 } from 'lucide-react';
import { OTBadge, OvertimeDetailDialog, overtimeErrorText } from './OvertimePanel';

export default function HRMSOvertime({ month }) {
  const { api, lang } = useApp();
  const zh = lang === 'zh';
  const T = (a, b) => zh ? a : b;
  const [context, setContext] = useState(null);
  const [mode, setMode] = useState('mine');
  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const sequence = useRef(0);
  const load = useCallback(async () => {
    const id = ++sequence.current;
    setLoading(true);
    try {
      const { data: ctx } = await api.get('/hrms/overtime/context');
      const last = new Date(Number(month.slice(0, 4)), Number(month.slice(5)), 0).getDate();
      const { data } = await api.get('/hrms/overtime', { params: { mode: ctx.canReview ? mode : 'mine', start: `${month}-01`, end: `${month}-${last}` } });
      if (id !== sequence.current) return;
      setContext(ctx); setRows(data); setError('');
    } catch (e) { if (id === sequence.current) setError(overtimeErrorText(e, '加班台账加载失败，请重试 / Could not load overtime.')); }
    finally { if (id === sequence.current) setLoading(false); }
  }, [api, month, mode]);
  useEffect(() => { load(); return () => { sequence.current++; }; }, [load]);
  return <section className="space-y-3" data-testid="hrms-overtime-ledger">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <p className="text-sm text-slate-600">{T('在“我的排班”申请加班；批准工时与实际打卡分开记录，异常需核对。', 'Apply in My Schedule. Approved hours and actual clocking are recorded separately; exceptions require review.')}</p>
      {context?.canReview && <select aria-label={T('加班记录范围', 'Overtime scope')} className="min-h-11 rounded-lg border border-slate-300 bg-white px-3 text-sm" value={mode} onChange={e => setMode(e.target.value)}><option value="mine">{T('我的加班', 'My overtime')}</option><option value="review">{T('可审批员工', 'Employees I can review')}</option></select>}
    </div>
    {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error} <button className="underline" onClick={load}>{T('重试', 'Retry')}</button></p>}
    {loading ? <p className="p-6 text-sm text-slate-500"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />{T('加载中…', 'Loading…')}</p> : !rows.length ? <p className="rounded-xl border bg-white p-6 text-sm text-slate-500">{T('本月没有加班记录', 'No overtime records this month')}</p> : <div className="grid gap-3 lg:grid-cols-2">{rows.map(r => <button key={r.id} onClick={() => setDetail(r)} className="rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-indigo-400 focus-visible:ring-2 focus-visible:ring-indigo-500">
      <span className="flex flex-wrap items-center justify-between gap-2"><b className="text-sm text-slate-900">{r.employeeName}</b><OTBadge row={r} zh={zh} /></span>
      <span className="mt-2 block text-sm text-slate-700">{r.startLocal.replace('T', ' ')} → {r.endLocal.replace('T', ' ')}</span>
      <span className="mt-1 block text-xs text-slate-500">{r.timeZone}</span>
      <span className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-700">{[[T('批准', 'Approved'), r.approvedHours], [T('实际', 'Actual'), r.actualHours], [T('待核对', 'To review'), r.pendingHours]].map(([label, n]) => <span key={label}>{label} <b>{Number(n || 0).toFixed(2)} h</b></span>)}</span>
      <span className="mt-2 block truncate text-sm text-slate-500">{r.reason}</span>
    </button>)}</div>}
    {detail && <OvertimeDetailDialog key={detail.id} api={api} zh={zh} row={detail} onClose={() => setDetail(null)} onChanged={load} />}
  </section>;
}
